import Backdrop from '@/components/ui/backdrop';
import { Location, SingleLocation } from '@/core/entities';
import AdminControls from '@/features/admin/AdminControls';
import UpdateLocationForm, {
	UpdateLocationFormModel,
} from '@/features/locations/UpdateLocationForm';
import LocationsManagement from '@/features/locations/locations-management/LocationsManagement';
import { useLocation } from '@/queries/locations';
import { useSite } from '@/queries/sites';
import { FIRST_PAGE, PAGINATION_META_DEFAULT } from '@/shared/constants/pagination';
import { useSearchQuery } from '@/shared/hooks/useSearchQuery';
import { PaginationMeta } from '@/shared/types/pagination';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import { Paper } from '@mui/material';
import { Box } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const LocationPage: FunctionComponent = (): ReactElement => {
	const { locationId } = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [location, setLocation] = useState<SingleLocation>();
	const [subLocations, setSubLocations] = useState<Location[]>([]);
	const { id, name, site, ancestors, subLevel } = location || {};

	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>(PAGINATION_META_DEFAULT);
	const { currentPage, pageSize } = paginationMeta;

	const [searchTerm] = useState('');
	const searchQuery = useSearchQuery(searchTerm);

	const [updateLocationForm, setUpdateLocationForm] = useState<{
		open: boolean;
		formModel: UpdateLocationFormModel;
		level?: 'location' | 'subLocation';
		location?: Location;
	}>({
		open: false,
		formModel: { name: '' },
	});

	const { queryKey: locationQueryKey, query: locationQuery } = useLocation({
		locationId,
		queryParams: { limit: pageSize, search: searchQuery },
	});

	const { data: locationData } = locationQuery;
	const { pages } = locationData || {};

	const refetchLocations = async () => {
		await queryClient.invalidateQueries(locationQueryKey);
	};
	const onSubLocationUpdated = async () => refetchLocations();
	const onLocationUpdated = async () => refetchLocations();

	useEffect(
		() => setPaginationMeta((paginationMeta) => ({ ...paginationMeta, currentPage: FIRST_PAGE })),
		[searchQuery]
	);

	useEffect(() => {
		const location = pages?.[currentPage - 1];
		if (location) setLocation({ ...location });
	}, [pages, currentPage]);

	useEffect(() => {
		const { subLocations } = pages?.find((page) => page.subLocations.page === currentPage) || {};
		if (subLocations) {
			const {
				data,
				page: currentPage,
				totalItems,
				totalPages,
				itemsPerPage: pageSize,
			} = subLocations;
			setSubLocations(data);
			setPaginationMeta({ currentPage, totalItems, totalPages, pageSize });
		}
	}, [pages, currentPage]);

	const { query: siteQuery } = useSite(site?.id);
	const { data: singleSite } = siteQuery;

	const breadcrumbs = useMemo(() => {
		if (!site || !ancestors) return [];
		return [
			{ title: 'Sites', path: '/sites' },
			{ title: site.name, path: `/sites/${site.id}/locations` },
			...[...ancestors].reverse().map((ancestor, key) => ({
				title: ancestor.name,
				path:
					key === ancestors.length - 1 ? undefined : `/sites/${site.id}/locations/${ancestor.id}`,
			})),
		];
	}, [site, ancestors]);

	if (!name || !subLevel || !singleSite) return <Backdrop />;

	return (
		<AdminPanelPage
			title={name}
			breadcrumbs={breadcrumbs}
			headerControls={
				<AdminControls
					edit={() =>
						setUpdateLocationForm({
							...updateLocationForm,
							location,
							level: 'location',
							formModel: { name },
							open: true,
						})
					}
				/>
			}
		>
			<Box component={Paper} padding={3}>
				<LocationsManagement
					level={subLevel}
					parentLocationId={id}
					site={singleSite}
					header={{}}
					table={{
						data: subLocations,
						rowAction: (location) => navigate(`/sites/${site!.id}/locations/${location.id}`),
						columnActions: {
							edit: (location) =>
								setUpdateLocationForm({
									...updateLocationForm,
									location,
									level: 'subLocation',
									formModel: { name: location.name },
									open: true,
								}),
						},
					}}
					invalidateData={refetchLocations}
				/>
			</Box>
			<UpdateLocationForm
				open={updateLocationForm.open}
				onClose={() => setUpdateLocationForm({ ...updateLocationForm, open: false })}
				location={updateLocationForm.location}
				level={updateLocationForm.level === 'location' ? location?.level : subLevel}
				formModel={updateLocationForm.formModel}
				onChangeFormModel={(field: keyof Location, value: string) =>
					setUpdateLocationForm({
						...updateLocationForm,
						formModel: { ...updateLocationForm.formModel, [field]: value },
					})
				}
				onSubmitted={
					updateLocationForm.level === 'location' ? onLocationUpdated : onSubLocationUpdated
				}
			/>
		</AdminPanelPage>
	);
};

export default LocationPage;
